import React, { useState, useEffect, useCallback } from 'react'; 
import { LoadScript } from '@react-google-maps/api';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const libraries = ['drawing'];

const App = () => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [authResolved, setAuthResolved] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [dispatchList, setDispatchList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    instance.handleRedirectPromise()
      .then(() => setAuthResolved(true))
      .catch(e => {
        console.error("MSAL redirect handling error:", e);
        setAuthResolved(true);
      });
  }, [instance]);

  useEffect(() => {
    if (authResolved && !isAuthenticated) {
      instance.loginRedirect().catch(err => {
        console.error("MSAL login error:", err);
      });
    }
  }, [authResolved, isAuthenticated, instance]);

  const userEmail = accounts[0]?.username?.toLowerCase() || null;

  useEffect(() => {
    if (!authResolved || !isAuthenticated) return;

    const fetchDrivers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/drivers`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = await res.json();
        setDrivers(data);
      } catch (err) {
        console.error("Error loading drivers:", err);
      }
    };

    fetchDrivers();
  }, [authResolved, isAuthenticated]);

  useEffect(() => {
    if (!userEmail || drivers.length === 0 || selectedDriver) return;

    const match = drivers.find(d =>
      d.Email?.toLowerCase() === userEmail
    );

    if (match) {
      console.log("✅ Auto-matched driver:", match.DriverName);
      autoSelectDriver(match);
    } else {
      console.warn("❌ No driver matched for email:", userEmail);
    }
  }, [userEmail, drivers, selectedDriver]);


  useEffect(() => {
    if (!userEmail || drivers.length === 0) return;
  
    // Only auto-select once
    if (selectedDriver === null) {
      const match = drivers.find(
        d => d.Email?.toLowerCase() === userEmail
      );
  
      if (match) {
        console.log("✅ Auto-matched driver:", match.DriverName);
        autoSelectDriver(match);
      } else {
        console.warn("❌ No driver matched for email:", userEmail);
      }
    }
  }, [userEmail, drivers]); 
  
  const formatDateMMDDYYYY = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${month}/${day}/${year}`;
  };
  
  const autoSelectDriver = useCallback(async (driver) => {
    setSelectedDriver(driver);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/driversdispatches?drivernumber=${driver.DriverNumber}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      const cleaned = data.map(dispatch => ({
        ...dispatch,
        coordinates: dispatch.lat && dispatch.lon ? { lat: dispatch.lat, lng: dispatch.lon } : null
      }));
      setDispatchList(cleaned);
    } catch (error) {
      console.error("Error fetching dispatches:", error);
    }
    setLoading(false);
  }, []);

  const handleDriverChange = (e) => {
    const driverNumber = parseInt(e.target.value, 10);
    const driver = drivers.find(d => d.DriverNumber === driverNumber);
    if (driver) autoSelectDriver(driver);
    else {
      setSelectedDriver(null);
      setDispatchList([]);
    }
  };

  const handleDispatchClick = async (dispatch) => {
    const { DispatchID, LeaseNumber, ProducerID, coordinates, LeaseName, ProducerName } = dispatch;

    if (!coordinates?.lat || !coordinates?.lng) {
      alert("There are no map coordinates set up for this lease.");
      return;
    }

    // Save current URL to return to
    const currentPageUrl = window.location.href;

    // 1️⃣ Trigger KMZ download
    const kmzUrl = `${API_BASE_URL}/generatekmz?producerId=${ProducerID}&leaseNumber=${LeaseNumber}&lat=${coordinates.lat}&lng=${coordinates.lng}&leaseName=${encodeURIComponent(
      LeaseName
    )}&producerName=${encodeURIComponent(ProducerName)}`;
    const link = document.createElement("a");
    link.href = kmzUrl;
    link.download = `${LeaseName || 'dispatch'}.kmz`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 2️⃣ Fetch dispatch report data
    try {
      const res = await fetch(`${API_BASE_URL}/dispatchreport?dispatchID=${DispatchID}`);
      if (!res.ok) throw new Error("Failed to fetch report data");
      const { header, details } = await res.json();

      // 3️⃣ Open report in a new tab
      const newWindow = window.open("", "_blank");
      if (!newWindow) return alert("Popups are blocked!");

      const detailRows = details
        .map((d, i) => `
          <div class="detail-stop">
            <div class="detail-titles">
              <div><b>Tank #</b></div><div><b>Tank ID2</b></div><div><b>Lease</b></div><div><b>Farm</b></div><div><b>Producer</b></div>
              <div><b>Call Date</b></div><div><b>Tank Size</b></div><div><b>Comments</b></div><div><b>Ft</b></div><div><b>In</b></div><div><b>Est BBL</b></div>
            </div>
            <div class="detail-row">
              <div>${d.RunTicketTankID || ""}</div>
              <div>${d.RunTicketTankID2 || ""}</div>
              <div>${d.LeaseName || ""}</div>
              <div>${d.RunTicketFarmNumber || ""}</div>
              <div>${d.ProducerName || ""}</div>
              <div>${formatDateMMDDYYYY(d.RunTicketCallDate) || ""}</div>
              <div>${d.RunTicketTankSize || ""}</div>
              <div>${d.RunTicketRemarks || ""}</div>
              <div>${d.Feet ?? 0}</div>
              <div>${d.Inches ?? 0}</div>
              <div>${d.RunTicketEstBbls || ""}</div>
            </div>
            <div class="sub-detail">
              <div class="sub-detail-row"><strong>Tank Stenciled:</strong><span>${d.StencilIndYes ? "YES" : d.StencilIndNo ? "NO" : ""}</span></div>
              <div class="sub-detail-row"><strong>Special Instructions:</strong><span>${d.SpecialInstructions || ""}</span></div>
              <div class="sub-detail-row"><strong>Combo/Key:</strong><span>${d.ComboKey || ""}</span></div>
              <div class="sub-detail-row"><strong>Gauged?:</strong><span>${d.GaugeableIndYes ? "YES" : d.GaugeableIndNo ? "NO" : ""}</span></div>
            </div>
            ${i < details.length - 1 ? '<hr class="stop-separator">' : ''}
          </div>
        `).join("");

      const html = `
        <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Dispatch Report ${header.DispatchID}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 20px; background:#f8f9fb; color:#222; }
            .report-container { background:white; border-radius:10px; padding:25px; max-width:1100px; margin:auto; position:relative; }
            h1 { text-align:center; color:#004080; font-size:24px; margin-bottom:15px; }

            /* Fixed back button */
            .back-button {
              position: fixed;
              top: 15px;
              left: 15px;
              background-color: #004080;
              color: white;
              text-decoration: none;
              padding: 8px 14px;
              border-radius: 6px;
              font-weight: bold;
              z-index: 9999;
            }

            .detail-titles, .detail-row { display:grid; grid-template-columns:repeat(11,1fr); gap:10px; margin-bottom:5px; }
            .detail-row div, .detail-titles div { padding:4px 8px; border-radius:4px; font-size:13px; }
            .sub-detail { margin-left:15px; margin-top:5px; background:#f9fcff; border-left:3px solid #007bff; padding:8px 12px; border-radius:4px; font-size:13px; }
            .sub-detail-row { display:flex; gap:10px; margin-bottom:4px; }
            .sub-detail-row strong { width:150px; flex-shrink:0; text-align:right; padding-right:12px; }
            .sub-detail-row span { flex:1; text-align:left; }
            .stop-separator { border:1px solid #ccc; margin:10px 0; }
          </style>
        </head>
        <body>
          <a href="${currentPageUrl}" class="back-button">← Back to Dispatches</a>
          <div class="report-container">
            <h1>ARG Dispatch Report</h1>
            <h3>Dispatch #${header.DispatchID} — ${header.DispatchDesc || ""}</h3>

            <table class="header-table">
              <tr><td><b>Est BBL:</b></td><td>${header.Estbbls || ""}</td></tr>
              <tr><td><b>Req Date:</b></td><td>${header.ReqDate ? new Date(header.ReqDate).toLocaleDateString() : ""}</td></tr>
              <tr><td><b>DRV:</b></td><td>${header.DriverInitials || ""}</td></tr>
              <tr><td><b>County:</b></td><td>${header.County || ""}</td></tr>
              <tr><td><b>Approx Hours:</b></td><td>${header.ApproxHours || ""}</td></tr>
              <tr><td><b>Instructions:</b></td><td>${header.Remarks || ""}</td></tr>
              <tr><td><b>Number of Stops:</b></td><td>${header.NumberOfStops || ""}</td></tr>
            </table>
    
            <div class="subheading"></div>
    
            <table>

              <tbody>${detailRows}</tbody>
              </table>
  
            </div>
        </body>
        </html>
      `;

      newWindow.document.write(html);
      newWindow.document.close();

    } catch (err) {
      console.error("❌ Error opening dispatch report:", err);
      alert("Failed to open dispatch report");
    }
  };

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: 'http://localhost:3000/api'
    });
  };

  if (!authResolved) return <div>Loading authentication...</div>;
  if (!isAuthenticated) return <div>Redirecting to login...</div>;

  const dispatchGroups = dispatchList.reduce((acc, dispatch) => {
    if (!acc[dispatch.DispatchID]) acc[dispatch.DispatchID] = [];
    acc[dispatch.DispatchID].push(dispatch);
    return acc;
  }, {});

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
      <div className="centered-container">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img src="/3DeckLogoRefineEverything.jpg" alt="Company Logo" style={{ maxWidth: '200px' }} />
        </div>

        {!selectedDriver && (
          <select value={selectedDriver?.DriverNumber || ''} onChange={handleDriverChange}>
            <option value="">-- Select Driver --</option>
            {drivers.map(driver => (
              <option key={driver.DriverNumber} value={driver.DriverNumber}>
                {driver.DriverName}
              </option>
            ))}
          </select>
        )}

        {loading && <p>Loading dispatches...</p>}

        {dispatchList.length > 0 && selectedDriver && (
          <>
            <h2>Dispatches for {selectedDriver.DriverName}</h2>
            <div className="grid-container">
              {dispatchList.map(dispatch => {
                const group = dispatchGroups[dispatch.DispatchID];
                const stopIndex = group.indexOf(dispatch) + 1;
                const totalStops = group.length;

                return (
                  <div
                    key={dispatch.DispatchKey}
                    className="dispatch-card"
                    onClick={() => handleDispatchClick(dispatch)}
                  >
                    <h3>Dispatch #{dispatch.DispatchID}</h3>
                    <p>Stop {stopIndex} of {totalStops}</p>
                    <p>{dispatch.LeaseName} - {dispatch.LeaseNumber}</p>
                    <p>Tank # {dispatch.RunTicketTankID}</p>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={{ marginTop: '40px', textAlign: 'center' }}>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>
      </div>
    </LoadScript>
  );
};

export default App;
